import { Component, Input, forwardRef, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'app-form-location',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section">
      <label [for]="id" class="inline-label" [class.required-field]="required">{{ label }}
        <button type="button" class="small-button" (click)="getCurrentLocation()">{{ 'form.location.get_location' | translate }}</button>
      </label>
      <input
        type="text"
        [id]="id"
        [(ngModel)]="value"
        (ngModelChange)="onChange($event)"
        [name]="name"
        [placeholder]="placeholder"
      />
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormLocationComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormLocationComponent implements ControlValueAccessor {
  @Input() label: string = '';  // 標籤
  @Input() id: string = '';     // id 用於對應 label
  @Input() name: string = '';   // name 用於表單提交
  @Input() placeholder: string = ''; // 佔位符
  @Input() required: boolean = false; // 是否必填

  @Output() locationChange = new EventEmitter<string>(); // 用於通知父元件變更位置

  value: string = '';  // 綁定的值

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private i18nService: I18nService) {}

  // 當使用者點擊按鈕時，根據當前位置設置位置
  getCurrentLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          this.value = `${latitude}, ${longitude}`;
          this.locationChange.emit(this.value);
          // 通知表單控管機制
          this.onChange(this.value);
        },
        (error) => {
          const errorMessage = this.i18nService.translate('form.location.gps_error');
          alert(errorMessage);
        }
      );
    } else {
      const errorMessage = this.i18nService.translate('form.location.browser_not_support');
      alert(errorMessage);
    }
  }

  // 用於 ControlValueAccessor 實作
  writeValue(value: any): void {
    console.log('[DEBUG] FormLocationComponent writeValue:', value);
    
    if (value !== undefined && value !== null) {
      // 處理動態表單編輯模式的資料結構
      if (typeof value === 'object') {
        // 處理巢狀結構 { type: 'location', value: {...} }
        if (value.type === 'location' && value.value) {
          if (value.value.originalValue) {
            // 如果有 originalValue，使用它
            this.value = String(value.value.originalValue);
          } else if (value.value.address) {
            // 如果有 address，使用它
            this.value = value.value.address;
          } else {
            // 嘗試從其他屬性組合
            this.value = value.value.summary || JSON.stringify(value.value);
          }
        }
        // 處理簡單結構 { type: 'location', originalValue: ... }
        else if (value.type === 'location' && value.originalValue) {
          this.value = String(value.originalValue);
        }
        // 處理包含座標的物件
        else if (value.latitude && value.longitude) {
          this.value = `${value.latitude}, ${value.longitude}`;
        }
        // 處理包含 lat/lng 的物件
        else if (value.lat && value.lng) {
          this.value = `${value.lat}, ${value.lng}`;
        }
        // 處理包含 address 的物件
        else if (value.address) {
          this.value = value.address;
        }
        // 如果是其他物件格式，嘗試轉換為字串
        else {
          this.value = JSON.stringify(value);
        }
      } else {
        // 如果已經是字串，直接使用
        this.value = String(value);
      }
    } else {
      this.value = '';
    }
    
    console.log('[DEBUG] FormLocationComponent processed value:', this.value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 如有需要，可以處理 disabled 狀態
  }
}
